import { useAppContext } from '../context/AppContext';

type RenderTextFn = (key: string, fallback?: string) => string | JSX.Element;
type RenderPlainTextFn = (key: string, fallback?: string) => string;
type GetTextFn = (key: string, fallback?: string) => string;
type GetTextForLangFn = (key: string, langCode: string, fallback?: string) => string;
type RenderTextForLangFn = (key: string, langCode: string, fallback?: string) => string | JSX.Element;

const CMS_KEY_MAP: Record<string, string> = {
  // ========== GENERAL / GLOBAL ==========
  app_name: "App Name",
  loading_text: "Loading Text",

  // Menu & Navigation
  menu_title: "Menu Title",
  menu_language_section: "Language Section Title",
  menu_links_section: "Quick Links Section Title",
  menu_footer_version: "Footer Version Text",
  menu_footer_powered: "Footer Powered By Text",
  banner_link1: "Banner Link 1",
  banner_link2: "Banner Link 2",

  // ========== INVITATION CODE SCREEN ==========
  invitation_code_title: "Invitation Code Title",
  invitation_code_subtitle: "Invitation Code Subtitle",
  invitation_code_label: "Invitation Code Label",
  invitation_code_placeholder: "Invitation Code Placeholder",
  invitation_code_button: "Invitation Code Button",
  invitation_code_error: "Invitation Code Error Message",

  // ========== ONBOARDING SCREEN ==========
  onboarding_header: "Onboarding Header",
  onboarding_button: "Onboarding Continue Button",

  // ========== HOME SCREEN ==========
  home_header: "Home Header",
  home_subtitle: "Home Subtitle",
  home_action1_title: "Home Action 1 Title",
  home_action1_subtitle: "Home Action 1 Subtitle",
  home_action2_title: "Home Action 2 Title",
  home_action2_subtitle: "Home Action 2 Subtitle",

  // Articles Section
  articles_section_title: "Articles Section Title",
  articles_see_all: "Articles See All",
  articles_read_more: "Articles Read More",

  // Collaboration Footer
  collaboration_text: "Collaboration Text",

  // ========== TEXT CHECKER SCREEN ==========
  text_checker_header: "Text Checker Header",
  text_checker_label: "Text Checker Input Label",
  text_checker_button_paste: "Text Checker Paste Button",
  text_checker_placeholder: "Remove placeholder text",

  // ========== IMAGE CHECKER SCREEN ==========
  image_checker_header: "Image Checker Header",
  image_checker_label: "Image Checker Label",
  image_checker_instruction: "Image Checker Instruction",
  image_checker_button_upload: "Image Checker Upload Button",

  // ========== WEB-SPECIFIC ==========
  navigation_back: "Navigation Back",
  drag_drop_text: "Drag and drop text",
  paste_text: "Paste text",

  // ========== ORIGIN SELECTION ==========
  origin_question: "Origin Question",
  origin_email: "Origin Option Email",
  origin_sms: "Origin Option SMS",
  origin_webpage: "Origin Option Webpage",
  origin_social_media: "Origin Option Social Media",
  origin_other: "Origin Option Other",
  origin_terms: "Origin Terms Acceptance",
  origin_button_check: "Origin Check Button",

  // ========== ANALYSIS SCREEN ==========
  analysis_loading_title: "Analysis Loading Title",
  analysis_loading_subtitle: "Analysis Loading Subtitle",
  analysis_cancel_button: "Analysis Cancel Button",

  // ========== RESULT SCREEN ==========
  result_risk_heading: "Result Risk Heading",
  result_warn_heading: "Result Warning Heading",
  result_safe_heading: "Result Safe Heading",
  result_case_id_label: "Result Case ID Label",
  result_details_title: "Result Analysis Details Title",
  result_button_customer_service: "Result Customer Service Button",
  result_button_check_new: "Result Check New Button",

  // ========== CUSTOMER SERVICE SCREEN ==========
  cs_header: "Customer Service Header",
  cs_info_text: "Customer Service Info Text",
  cs_email_label: "Customer Service Email Label",
  cs_email_placeholder: "Customer Service Email Placeholder",
  cs_phone_label: "Customer Service Phone Label",
  cs_phone_placeholder: "Customer Service Phone Placeholder",
  cs_description_label: "Customer Service Description Label",
  cs_description_placeholder: "Customer Service Description Placeholder",
  cs_button_send: "Customer Service Send Button",
  cs_button_back: "Customer Service Back Button",
  customer_service_required_field: "Customer Service Required Field",
  customer_service_call_us: "Customer Service Call Us",

  // ========== THANK YOU SCREEN ==========
  thankyou_message: "Thank You Message",
  thankyou_button_home: "Thank You Home Button",

  // ========== ARTICLES SCREEN ==========
  articles_page_title: "Articles Page Title",
  articles_available_text: "Articles Available Text",

  // ========== AI CONSENT MODAL ==========
  ai_consent_title: "AI Consent Title",
  ai_consent_intro: "AI Consent Intro",
  ai_consent_what_title: "AI Consent What Title",
  ai_consent_what_body: "AI Consent What Body",
  ai_consent_who_title: "AI Consent Who Title",
  ai_consent_who_body: "AI Consent Who Body",
  ai_consent_why_title: "AI Consent Why Title",
  ai_consent_why_body: "AI Consent Why Body",
  ai_consent_retention_title: "AI Consent Retention Title",
  ai_consent_retention_body: "AI Consent Retention Body",
  ai_consent_policy_note: "AI Consent Policy Note",
  ai_consent_accept: "AI Consent Accept Button",
  ai_consent_decline: "AI Consent Decline Button",

  // ========== WEB-SPECIFIC RESULT / QUESTIONS SECTION ==========
  response_screen_bold_2: "Response Contact Heading",
  response_screen_email: "Response Contact Email",
  response_screen_phone: "Response Contact Phone",
};

export const useCmsText = () => {
  const { state } = useAppContext();

  const getActualKey = (key: string): string => {
    return CMS_KEY_MAP[key] || key;
  };

  const pickCmsItemByLangCode = (langCode: string) => {
    if (!state.cmsData || !state.cmsData.cms) return null;
    return state.cmsData.cms.find((cmsItem: any) =>
      cmsItem.mappedProperties.some(
        (p: any) => p.name === 'Language' && p.value === langCode
      )
    );
  };

  const getText: GetTextFn = (key: string, fallback: string = key): string => {
    const actualKey = getActualKey(key);

    if (!state.cmsData || !state.cmsData.cms) return fallback;

    const matchedCmsItem = state.cmsData.cms.find(
      (cmsItem: any) =>
        typeof cmsItem.name === 'string' && cmsItem.name?.toLowerCase().includes(state.selectedLanguage.toLowerCase())
    );

    if (!matchedCmsItem) return fallback;

    const prop = matchedCmsItem.mappedProperties.find(
      (p: any) => p.name === actualKey
    );

    return prop?.value || fallback;
  };

  const getTextForLanguage: GetTextForLangFn = (key, langCode, fallback = key) => {
    const actualKey = getActualKey(key);
    const languageMatchedCmsItem = pickCmsItemByLangCode(langCode);
    if (!languageMatchedCmsItem) return fallback;

    const prop = languageMatchedCmsItem.mappedProperties.find(
      (p: any) => p.name === actualKey
    );

    return prop?.value || fallback;
  };

  const renderTextForLanguage: RenderTextForLangFn = (key, langCode, fallback = key) => {
    const text = getTextForLanguage(key, langCode, fallback);

    if (text.includes('<a') || text.includes('<br')) {
      return <span style={{ color: state.branding?.fontColor }} dangerouslySetInnerHTML={{ __html: text }} />;
    }

    return text;
  };

  const renderText: RenderTextFn = (key: string, fallback: string = key): string | JSX.Element => {
    const text = getText(key, fallback);

    if (text.includes('<a') || text.includes('<br')) {
      return <span style={{ color: state.branding?.fontColor }} dangerouslySetInnerHTML={{ __html: text }} />;
    }

    return text;
  };

  const renderPlainText: RenderPlainTextFn = (key: string, fallback: string = key): string => {
    return getText(key, fallback);
  };

  return {
    renderText,
    renderPlainText,
    getText,
    getTextForLanguage,
    renderTextForLanguage,
  };
};
